<template>
  <panel-layout
    id="campaign-panel"
    :loading="loading"
    :show-footer="false"
    :shouldDismiss="() => !unsavedChanges"
    :didAttemptToDismiss="cancel"
  >
    <template #heading>
      <h2 class="text-lg md:text-xl tracking-tight font-bold leading-none mb-1">
        {{ $t('pages.marketing.campaigns.view.title') }}
      </h2>

      <o-chip v-if="campaign && campaign.isCampaignTypeSMS" flat size="xs">
        {{ $t('general.sms') }}
      </o-chip>

      <o-chip v-if="campaign && campaign.isCampaignTypeEmail" flat size="xs">
        {{ $t('general.email') }}
      </o-chip>
    </template>

    <div v-if="campaign" class="h-full">
      <o-scroll class="bg-gray-50 dark:bg-gray-900">
        <div class="h-full flex flex-col">
          <div class="flex flex-col divide-y pt-6 pb-12">
            <o-form ref="form">
              <div class="flex items-center py-2 px-6">
                <p class="opacity-60 text-sm font-medium w-32 flex-shrink-0">
                  {{ $t('pages.marketing.campaigns.view.customerCount') }}
                </p>
                <div class="flex-grow">
                  <p class="focus:outline-none leading-none opacity-80 mr-4 line-clamp-1 text-xs">
                    {{ campaign.customerCount }}
                  </p>
                </div>
              </div>

              <div class="flex items center py-2 px-6">
                <p class="opacity-60 text-sm font-medium w-32 flex-shrink-0">
                  {{ $t('general.status') }}
                </p>
                <div class="flex-grow">
                  <o-chip v-if="campaign.isCampaignStatusQueued" variant="warning" size="xs">
                    {{ $t('general.queued') }}
                  </o-chip>

                  <o-chip v-if="campaign.isCampaignStatusSending" variant="info" size="xs">
                    {{ $t('general.sending') }}
                  </o-chip>

                  <o-chip v-if="campaign.isCampaignStatusSent" variant="success" size="xs">
                    {{ $t('general.sent') }}
                  </o-chip>

                  <o-chip v-if="campaign.isCampaignStatusError" variant="danger" size="xs">
                    {{ $t('general.error') }}
                  </o-chip>

                  <o-chip v-if="campaign.isCampaignStatusDraft" variant="warning" size="xs">
                    {{ $t('general.draft') }}
                  </o-chip>

                  <o-chip v-if="campaign.isCampaignStatusScheduled" variant="info" size="xs">
                    {{ $t('general.scheduled') }}
                  </o-chip>
                </div>
              </div>

              <div v-if="campaign.isCampaignStatusSent || campaign.isCampaignStatusScheduled" class="flex items center py-2 px-6">
                <p class="opacity-60 text-sm font-medium w-32 flex-shrink-0">
                  {{ campaign.isCampaignStatusSent ? $t('general.sent') : $t('general.scheduled') }}
                </p>
                <div class="flex-grow">
                  <p v-if="campaign.sentDate" class="focus:outline-none leading-none opacity-80 mr-4 line-clamp-1 text-xs mt-2">
                    {{ $date.format($date.local(campaign.sentDate * 1000), $date.presets.full) }}

                    <span v-if="campaign.scheduledDateLabel">
                      {{ $t('marketing.scheduled', { date: campaign.scheduledDateLabel }) }}
                    </span>
                  </p>

                  <p v-if="!campaign.sentDate && campaign.isCampaignStatusScheduled" class="focus:outline-none leading-none opacity-80 mr-4 line-clamp-1 text-xs mt-2">
                    {{ $t('marketing.scheduled', { date: campaign.scheduledDateLabel }) }}
                  </p>
                </div>
              </div>

              <div
                v-if="isCampaignTypeEmail"
                class="flex items-center py-2 px-6"
              >
                <p class="opacity-60 text-sm font-medium w-32 flex-shrink-0">
                  {{ $t('general.subject') }}
                </p>
                <div class="flex-grow">
                  <o-field-group v-slot="{ status }" rules="required">
                    {{ campaign.subject }}
                  </o-field-group>
                </div>
              </div>

              <div class="flex items-start py-2 px-6">
                <p class="opacity-60 text-sm font-medium w-32 flex-shrink-0">
                  {{ $t('general.type') }}
                </p>
                <div class="flex-grow flex items-center hover">
                  <div class="text-sm flex-grow">
                    <template v-if="campaign.isContentTypeMarketing">
                      <p>{{ $t('general.marketing') }}</p>
                      <p class="text-gray-500 dark:text-gray-400">
                        {{ $t('marketing.notToUnsubscribed') }}
                      </p>
                    </template>

                    <template v-if="campaign.isContentTypeAdministrative">
                      <p>{{ $t('marketing.administrative') }}</p>
                      <p class="text-gray-500 dark:text-gray-400">
                        {{ $t('marketing.sendToAll') }}
                      </p>
                    </template>
                  </div>
                </div>
              </div>

              <div v-if="isCampaignTypeEmail" class="flex items center py-2 px-6">
                <p class="opacity-60 text-sm font-medium w-32 flex-shrink-0">
                  {{ $t('pages.marketing.campaigns.view.attachments') }}
                </p>
                <div class="flex-grow">
                  <div v-if="!campaign.attachments?.length" class="text-gray-500 dark:text-gray-400">
                    {{ $t('pages.marketing.campaigns.view.noAttachments') }}
                  </div>

                  <div v-else class="flex items-start">
                    <div
                      v-for="attachment in campaign.attachments"
                      :key="attachment.guid"
                    >
                      <o-tooltip :text="attachment.name">
                        <div
                          class="h-18 w-24 rounded-md bg-cover mr-4 relative bg-white dark:bg-gray-900 border"
                        >
                          <div
                            v-if="attachment.type.includes('image')"
                            class="h-full w-full relative rounded-md bg-center bg-cover"
                            :style="{
                              backgroundImage: `url(${attachment.url})`,
                              backgroundRepeat: 'no-repeat',
                              backgroundPosition: 'center'
                            }"
                          />
                          <div
                            v-else
                            class="h-full w-full flex flex-col items-center justify-center"
                          >
                            <o-icon icon="file" class="text-gray-500 dark:text-gray-400 mb-2" />
                            <p
                              class="text-xs line-clamp-1 opacity-60 leading-none mb-1"
                            >
                              {{ attachment.name }}
                            </p>
                          </div>
                        </div>
                      </o-tooltip>
                    </div>
                  </div>
                </div>
              </div>
            </o-form>
          </div>
          <div v-if="isCampaignTypeEmail" class="p-6 border mx-6 rounded dark:border-gray-800 bg-white dark:bg-gray-900">
            <div v-html="campaign.body" />
          </div>
          <div v-if="isCampaignTypeSMS" class="p-6 border mx-6 rounded dark:border-gray-800 bg-white dark:bg-gray-900">
            {{ campaign.body }}
          </div>
        </div>
      </o-scroll>
    </div>
  </panel-layout>
</template>
<script setup lang="ts">
// @ts-ignore
import PanelLayout from '@/components/panel/PanelLayout.vue'

import { usePanel } from '~/composables/usePanel'

const { $api, $notification } = useNuxtApp()
const router = useRouter()

const panel = usePanel()

const props = defineProps<{ id?: string }>()

const campaign = ref<any>(null)
const loading = ref(true)

const isCampaignTypeEmail = computed(() => {
  return campaign.value.isCampaignTypeEmail
})

const isCampaignTypeSMS = computed(() => {
  return campaign.value.isCampaignTypeSMS
})

const fetchCampaign = async (id?: string) => {
  loading.value = true
  try {
    let c
    if (id) {
      c = (await $api.campaign.get(id)).data
    }

    campaign.value = c
    loading.value = false
  } catch (e: any) {
    console.log(e)
    $notification.error({ errors: e?.errors })
    router.push('/marketing/campaigns')
  }
}

watch(() => props.id, (id) => fetchCampaign(id), { immediate: true })
</script>
